export interface TreatmentPlan {
  situacao: string;
  condutaPrincipal: string;
  suporte: string;
  antibioticos: string;
  aines?: string[];
}

export const AINES_RECOMENDADOS = [
  "Flunixin meglumine",
  "Meloxicam",
  "Ketoprofen",
  "Dipirona"
];

function normalizeOrganism(input: string): string {
  const s = (input || "")
    .toLowerCase()
    .replace(/[\.]|\s+/g, " ")
    .replace("não", "nao")
    .trim();

  // Mapeia por keywords para um nome canônico
  if (s.includes("streptococcus") && s.includes("agalact")) return "Streptococcus agalactiae";
  if (s.includes("strep") && s.includes("agalact")) return "Streptococcus agalactiae";
  if (s.includes("streptococcus") && s.includes("dysgalact")) return "Streptococcus dysgalactiae";

  if (s.includes("streptococcus") && s.includes("uberis")) return "Streptococcus uberis";
  if (s.includes("strep") && s.includes("uberis")) return "Streptococcus uberis";

  if (s.includes("staphylococcus") && s.includes("aureus")) return "Staphylococcus aureus";
  if (s.includes("staph") && s.includes("aureus")) return "Staphylococcus aureus";

  if ((s.includes("coagulase") && s.includes("negativa")) || s.includes("con")) return "Staphylococcus coagulase negativa";
  if (s.includes("staphylococcus") && s.includes("spp")) return "Staphylococcus spp.";

  if (s.includes("escherichia") || s.includes("e coli")) return "Escherichia coli";

  if (s.includes("klebsiella") && s.includes("spp")) return "Klebsiella spp.";
  if (s.includes("kesk") || s.includes("kesc") || (s.includes("klebsiella") && s.includes("enterobacter") && s.includes("serratia") && s.includes("citrobacter"))) {
    return "Grupo KESK (Klebsiella, Enterobacter, Serratia, Citrobacter)";
  }

  if (s.includes("pmp") || (s.includes("proteus") && s.includes("morganella") && s.includes("providencia"))) {
    return "Grupo PMP (Proteus, Morganella, Providencia)";
  }

  if (s.includes("saprophyticus")) return "Staphylococcus saprophyticus";

  if (s.includes("candida") || s.includes("nao fermentadores") || s.includes("não fermentadores")) {
    return "Candida spp.";
  }

  if (s.includes("enterococcus") && s.includes("faecalis")) return "Enterococcus faecalis";

  if (s.includes("trueperella") && s.includes("pyogenes")) return "Trueperella pyogenes";

  return input || "";
}

const TREATMENTS: Record<string, TreatmentPlan> = {
  "Streptococcus agalactiae": {
    situacao: "Alta taxa de cura.",
    condutaPrincipal: "Recomenda-se realizar ordenhas frequentes para remoção do leite contaminado, reduzindo a carga bacteriana no úbere. Utilizar anti-inflamatório não esteroidal (AINE) nos casos em que houver dor ou edema.",
    suporte: "Não é necessário.",
    antibioticos: "Penicilina, amoxicilina, cefalexina ou cloxacilina, preferencialmente por via intramamária.",
    aines: AINES_RECOMENDADOS,
  },
  "Streptococcus dysgalactiae": {
    situacao: "Alta taxa de cura.",
    condutaPrincipal: "Ordenhas frequentes para remoção do leite contaminado; AINE em casos de dor/edema.",
    suporte: "Não é necessário.",
    antibioticos: "Penicilina, amoxicilina, cefalexina ou cloxacilina intramamária.",
    aines: AINES_RECOMENDADOS,
  },
  "Streptococcus uberis": {
    situacao: "Resposta terapêutica variável.",
    condutaPrincipal: "Realizar ordenhas frequentes e administrar AINE para controle da inflamação.",
    suporte: "Não é necessário.",
    antibioticos: "Penicilina ou cefalosporinas intramamárias.",
    aines: AINES_RECOMENDADOS,
  },
  "Staphylococcus aureus": {
    situacao: "Infecção de difícil cura, com baixa taxa de resposta ao tratamento.",
    condutaPrincipal: "Manter ordenhas frequentes e uso de AINE para alívio dos sinais clínicos.",
    suporte: "Não indicado.",
    antibioticos: "Cloxacilina ou cefalosporinas. O tratamento apresenta melhores resultados durante o período seco.",
    aines: AINES_RECOMENDADOS,
  },
  "Staphylococcus coagulase negativa": {
    situacao: "Frequentemente autolimitante (tende à cura espontânea).",
    condutaPrincipal: "Manter ordenhas frequentes e utilizar AINE leve, se necessário.",
    suporte: "Não necessário.",
    antibioticos: "Normalmente não indicados; em casos persistentes, pode-se utilizar cefalosporina intramamária.",
    aines: AINES_RECOMENDADOS,
  },
  "Staphylococcus spp.": {
    situacao: "Frequentemente autolimitante.",
    condutaPrincipal: "Ordenha frequente e AINE leve, quando necessário.",
    suporte: "Não necessário.",
    antibioticos: "Geralmente não indicados; se persistente, considerar cefalosporina intramamária.",
    aines: AINES_RECOMENDADOS,
  },
  "Escherichia coli": {
    situacao: "A recuperação depende do suporte clínico e do estado geral do animal.",
    condutaPrincipal: "Realizar ordenhas frequentes para eliminação do leite contaminado e aplicar AINEs para controle da inflamação.",
    suporte: "Reidratação com fluidos e fornecimento de suporte energético.",
    antibioticos: "Em geral, não são necessários; em casos graves, pode-se utilizar ceftiofur por via sistêmica.",
    aines: AINES_RECOMENDADOS,
  },
  "Klebsiella spp.": {
    situacao: "Prognóstico desfavorável, com baixa taxa de cura.",
    condutaPrincipal: "Realizar ordenhas frequentes e uso de AINEs.",
    suporte: "Administração agressiva de fluidos e suporte energético.",
    antibioticos: "Resposta terapêutica limitada; em muitos casos, o descarte do animal é indicado.",
    aines: AINES_RECOMENDADOS,
  },
  "Grupo KESK (Klebsiella, Enterobacter, Serratia, Citrobacter)": {
    situacao: "Prognóstico reservado a ruim.",
    condutaPrincipal: "Manter ordenhas frequentes e uso de AINEs.",
    suporte: "Aplicar fluidos de forma intensiva e fornecer suporte energético.",
    antibioticos: "Baixa resposta ao tratamento; pode-se tentar ceftiofur sistêmico, embora o descarte do animal muitas vezes seja a melhor alternativa.",
    aines: AINES_RECOMENDADOS,
  },
  "Grupo PMP (Proteus, Morganella, Providencia)": {
    situacao: "Prognóstico reservado.",
    condutaPrincipal: "Realizar ordenhas frequentes e uso de AINEs.",
    suporte: "Reidratação e suporte energético.",
    antibioticos: "Resposta geralmente limitada; em casos graves, pode-se empregar ceftiofur ou enrofloxacina por via sistêmica. O descarte deve ser considerado conforme evolução clínica.",
    aines: AINES_RECOMENDADOS,
  },
  "Staphylococcus saprophyticus": {
    situacao: "Mastite leve e autolimitante.",
    condutaPrincipal: "Realizar ordenhas frequentes e uso de AINE leve.",
    suporte: "Não necessário.",
    antibioticos: "Geralmente não indicados; em casos persistentes, pode-se utilizar cefalexina ou cefapirina intramamária.",
    aines: AINES_RECOMENDADOS,
  },
  "Candida spp.": {
    situacao: "Mastite micótica, comumente associada ao uso prolongado de antibióticos.",
    condutaPrincipal: "Suspender imediatamente o uso de antibióticos e realizar ordenhas frequentes.",
    suporte: "Aplicar antisséptico leve nos tetos (iodo ou clorexidina diluída).",
    antibioticos: "Contraindicados. Pode-se utilizar nistatina intramamária, quando disponível. O processo de recuperação é lento.",
    aines: AINES_RECOMENDADOS,
  },
  "Enterococcus faecalis": {
    situacao: "Prognóstico variável, geralmente com baixa resposta terapêutica.",
    condutaPrincipal: "Realizar ordenhas frequentes e uso de AINE.",
    suporte: "Não necessário.",
    antibioticos: "Devido à alta resistência, pode-se tentar amoxicilina associada ao ácido clavulânico ou cefalexina intramamária, embora a resposta seja limitada.",
    aines: AINES_RECOMENDADOS,
  },
  "Trueperella pyogenes": {
    situacao: "Mastite purulenta de caráter crônico.",
    condutaPrincipal: "Realizar ordenhas frequentes, drenagem do material purulento e uso de AINE.",
    suporte: "Não necessário.",
    antibioticos: "Não indicados; geralmente recomenda-se o descarte do quarto mamário afetado ou do animal.",
    aines: AINES_RECOMENDADOS,
  },
};

export function getTreatmentByOrganism(organism: string): TreatmentPlan | null {
  const canonical = normalizeOrganism(organism);
  return TREATMENTS[canonical] || null;
}